import { useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { MessageDescriptor } from 'react-intl';
import { useMessagingStore, useChatRehydration } from '../hooks/index.ts';
import { useMessagingService } from '../hooks/useMessagingService.ts';
import { StageHeader, type AgentLogo } from './StageHeader.tsx';
import { StageLayout } from './StageLayout.tsx';
import { StageVoice } from './StageVoice.tsx';
import { useStageController } from './useStageController.ts';
import { useStageAppearance } from './useStageAppearance.ts';
import { useA2uiDemoHook } from './demo-fixture.ts';

export interface StageShellProps {
  logo?: AgentLogo;
  appearance?: 'light' | 'dark' | 'auto';
  placeholder?: MessageDescriptor;
  voiceEnabled?: boolean;
}

/** Standalone entry with the normal agent arrival and parent preview integration. */
export const StageShell = observer(function StageShell({
  logo,
  appearance = 'light',
  placeholder,
  voiceEnabled = true,
}: StageShellProps) {
  useMessagingService();
  useStageAppearance(appearance);
  useA2uiDemoHook();
  const store = useMessagingStore();
  const [initError, setInitError] = useState(false);
  const onError = useCallback(() => setInitError(true), []);
  const connection = useChatRehydration({ onError });
  const stage = useStageController({
    connection: { ...connection, initError },
    canSubmit: connection.isReady && connection.connectionStatus === 'connected',
    submitMessage: store.sendMessage,
  });
  return (
    <div className="stage-frame">
      <StageLayout
        stage={stage}
        placeholder={placeholder}
        voiceEnabled={voiceEnabled}
        header={
          <StageHeader
            logo={logo}
            onRestart={stage.sendMessage}
            disabled={stage.runInFlight || !stage.ready}
          />
        }
      >
        {voiceEnabled ? <StageVoice screenSelection={stage.voiceScreenSelection} onRecord={stage.sendAudio} /> : null}
      </StageLayout>
    </div>
  );
});
